import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Menu, X } from 'lucide-react'
import { motion } from 'framer-motion'

interface NavigationItem {
  name: string
  href: string
}

interface AnnouncementBanner {
  text: string
  linkText: string
  linkHref: string
}

interface CallToAction {
  text: string
  href: string
  variant: 'primary' | 'secondary'
}

interface HeroLandingProps {
  logo?: {
    src: string
    alt: string
    companyName: string
  }
  navigation?: NavigationItem[]
  loginText?: string
  loginHref?: string
  title: string
  description: string
  announcementBanner?: AnnouncementBanner
  callToActions?: CallToAction[]
  titleSize?: 'small' | 'medium' | 'large'
  gradientColors?: {
    from: string
    to: string
  }
  className?: string
}

const defaultProps: Partial<HeroLandingProps> = {
  loginText: "Log in",
  loginHref: "#",
  titleSize: "large",
  gradientColors: {
    from: "oklch(0.646 0.222 41.116)",
    to: "oklch(0.488 0.243 264.376)"
  },
}

function SmartLink({ href, children, className }: { href: string; children: React.ReactNode; className?: string }) {
  if (href.startsWith('/') || href.startsWith('?')) {
    return <Link to={href} className={className}>{children}</Link>
  }
  return <a href={href} className={className}>{children}</a>
}

export function HeroLanding(props: HeroLandingProps) {
  const {
    logo,
    navigation,
    loginText,
    loginHref,
    title,
    description,
    announcementBanner,
    callToActions,
    titleSize,
    gradientColors,
    className
  } = { ...defaultProps, ...props }

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const getTitleSizeClasses = () => {
    switch (titleSize) {
      case 'small':
        return 'text-2xl sm:text-3xl md:text-5xl'
      case 'medium':
        return 'text-2xl sm:text-4xl md:text-6xl'
      case 'large':
      default:
        return 'text-3xl sm:text-5xl md:text-7xl'
    }
  }

  const renderCallToAction = (cta: CallToAction, index: number) => {
    if (cta.variant === 'primary') {
      return (
        <SmartLink
          key={index}
          href={cta.href}
          className="rounded-md bg-primary px-3.5 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary transition-colors"
        >
          {cta.text}
        </SmartLink>
      )
    } else {
      return (
        <SmartLink
          key={index}
          href={cta.href}
          className="text-sm/6 font-semibold text-foreground hover:text-primary transition-colors"
        >
          {cta.text} <span aria-hidden="true">→</span>
        </SmartLink>
      )
    }
  }

  return (
    <div className={`bg-background ${className || ''}`}>
      {/* Top gradient background */}
      <div
        className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80"
        aria-hidden="true"
      >
        <div
          className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] opacity-30 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]"
          style={{
            clipPath:
              'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
            background: `linear-gradient(to top right, ${gradientColors?.from}, ${gradientColors?.to})`,
          }}
        />
      </div>

      {/* Bottom gradient background */}
      <div
        className="absolute inset-x-0 top-[calc(100%-13rem)] -z-10 transform-gpu overflow-hidden blur-3xl sm:top-[calc(100%-30rem)]"
        aria-hidden="true"
      >
        <div
          className="relative left-[calc(50%+3rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 opacity-30 sm:left-[calc(50%+36rem)] sm:w-[72.1875rem]"
          style={{
            clipPath:
              'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
            background: `linear-gradient(to top right, ${gradientColors?.from}, ${gradientColors?.to})`,
          }}
        />
      </div>

      <motion.header
        className="fixed inset-x-0 top-0 z-50 bg-background/70 backdrop-blur-md border-b border-border/30"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <nav className="flex items-center justify-between p-4 lg:px-8 max-w-7xl mx-auto" aria-label="Global">
          <div className="flex lg:flex-1">
            <SmartLink href="/" className="-m-1.5 p-1.5">
              <span className="sr-only">{logo?.companyName}</span>
              <img className="h-8 w-auto" src={logo?.src} alt={logo?.alt} width={140} height={32} loading="eager" fetchPriority="high" decoding="async" />
            </SmartLink>
          </div>

          <div className="flex lg:hidden">
            <button
              type="button"
              aria-label="Abrir menu"
              onClick={() => setMobileMenuOpen(true)}
              className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-muted-foreground hover:text-foreground transition-colors"
            >
              <Menu className="size-6" aria-hidden="true" />
            </button>
          </div>
          {navigation && navigation.length > 0 && (
            <div className="hidden lg:flex lg:gap-x-12">
              {navigation.map((item) => (
                <SmartLink
                  key={item.name}
                  href={item.href}
                  className="text-sm/6 font-semibold text-foreground hover:text-primary transition-colors"
                >
                  {item.name}
                </SmartLink>
              ))}
            </div>
          )}
          {loginText && loginHref && (
            <div className="hidden lg:flex lg:flex-1 lg:justify-end">
              <SmartLink href={loginHref} className="text-sm/6 font-semibold text-foreground hover:text-primary transition-colors">
                {loginText} <span aria-hidden="true">&rarr;</span>
              </SmartLink>
            </div>
          )}
        </nav>
        <Dialog open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <DialogContent className="sm:max-w-sm w-full inset-y-0 right-0 left-auto top-0 translate-x-0 translate-y-0 h-full rounded-none data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right">
            <div className="flex items-center justify-between">
              <SmartLink href="/" className="-m-1.5 p-1.5">
                <span className="sr-only">{logo?.companyName}</span>
                <img className="h-8 w-auto" src={logo?.src} alt={logo?.alt} width={140} height={32} loading="lazy" decoding="async" />
              </SmartLink>
              <button
                type="button"
                aria-label="Fechar menu"
                onClick={() => setMobileMenuOpen(false)}
                className="-m-2.5 rounded-md p-2.5 text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="size-6" aria-hidden="true" />
              </button>
            </div>
            <div className="mt-6 flow-root">
              <div className="-my-6 divide-y divide-border">
                {navigation && navigation.length > 0 && (
                  <div className="space-y-2 py-6">
                    {navigation.map((item) => (
                      <SmartLink
                        key={item.name}
                        href={item.href}
                        className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-foreground hover:bg-muted transition-colors"
                      >
                        {item.name}
                      </SmartLink>
                    ))}
                  </div>
                )}
                {loginText && loginHref && (
                  <div className="py-6">
                    <SmartLink
                      href={loginHref}
                      className="-mx-3 block rounded-lg px-3 py-2.5 text-base/7 font-semibold text-foreground hover:bg-muted transition-colors"
                    >
                      {loginText}
                    </SmartLink>
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </motion.header>

      <div className="relative isolate px-6 pt-14 lg:px-8">
        <div className="mx-auto max-w-2xl py-20 sm:py-28 lg:py-36">
          {/* Announcement banner */}
          {announcementBanner && (
            <motion.div
              className="mb-8 flex justify-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <div className="relative rounded-full px-3 py-1 text-sm/6 text-muted-foreground ring-1 ring-border hover:ring-foreground/20 transition-colors">
                {announcementBanner.text}{' '}
                <SmartLink href={announcementBanner.linkHref} className="font-semibold text-primary">
                  <span className="absolute inset-0" aria-hidden="true" />
                  {announcementBanner.linkText} <span aria-hidden="true">&rarr;</span>
                </SmartLink>
              </div>
            </motion.div>
          )}

          <div className="text-center">
            <h1
              className={`font-semibold tracking-tight text-balance text-foreground ${getTitleSizeClasses()}`}
            >
              {title}
            </h1>
            <motion.p
              className="mt-8 text-lg font-medium text-pretty text-muted-foreground sm:text-xl/8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              {description}
            </motion.p>

            {/* Call to action buttons */}
            {callToActions && callToActions.length > 0 && (
              <motion.div
                className="mt-10 flex items-center justify-center gap-x-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.8 }}
              >
                {callToActions.map((cta, index) => renderCallToAction(cta, index))}
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export type { HeroLandingProps, NavigationItem, AnnouncementBanner, CallToAction }
